/**
 * Mobile menu drawer.
 * Full-width panel below header. Swaps hamburger ↔ X button on open/close.
 */
class HeaderDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-panel]');
    this.openBtn = this.querySelector('[data-open]');
    this.closeBtn = this.querySelector('[data-close]');

    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Escape dismisses the currency drop-up first, then the drawer itself
        if (this.currencySelector?.classList.contains('is-open')) {
          this.#toggleCurrency(false);
          this.currencySelector.querySelector('[data-mobile-currency-trigger]')?.focus();
          return;
        }
        if (this.isOpen) this.close();
      }
      if (e.key === 'Tab' && this.isOpen) this.#trapFocus(e);
    });

    // Currency selector in mobile menu
    this.currencySelector = this.querySelector('[data-mobile-currency-selector]');
    if (this.currencySelector) {
      const selector = this.currencySelector;
      const trigger = selector.querySelector('[data-mobile-currency-trigger]');
      const input = selector.closest('form')?.querySelector('[data-country-input]');

      trigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.#toggleCurrency(!selector.classList.contains('is-open'));
      });

      // Close when tapping anywhere outside the selector
      document.addEventListener('click', (e) => {
        if (!selector.contains(e.target)) this.#toggleCurrency(false);
      });

      selector.querySelectorAll('[data-country-code]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (input) {
            input.value = btn.dataset.countryCode;
            input.closest('form')?.submit();
          }
        });
      });
    }
  }

  #toggleCurrency(open) {
    if (!this.currencySelector) return;

    this.currencySelector.classList.toggle('is-open', open);
    this.currencySelector
      .querySelector('[data-mobile-currency-trigger]')
      ?.setAttribute('aria-expanded', String(open));

    // The trigger sits at the very bottom of a scrollable panel — make sure the
    // drop-up and the trigger are both fully in view once it opens.
    if (open) {
      this.currencySelector.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  get isOpen() {
    return this.panel?.classList.contains('is-open');
  }

  open() {
    // Position panel below the header
    const header = this.closest('header');
    if (header && this.panel) {
      this.panel.style.top = header.getBoundingClientRect().bottom + 'px';
    }

    this.panel?.classList.add('is-open');
    this.openBtn?.classList.add('hidden');
    this.closeBtn?.classList.remove('hidden');
    this.openBtn?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      this.closeBtn?.focus();
    }, 100);
  }

  close() {
    if (!this.isOpen) return;

    this.#toggleCurrency(false);
    this.panel?.classList.remove('is-open');
    this.openBtn?.classList.remove('hidden');
    this.closeBtn?.classList.add('hidden');
    this.openBtn?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.openBtn?.focus();
  }

  #trapFocus(e) {
    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

customElements.define('header-drawer', HeaderDrawer);

export { HeaderDrawer };
